import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterOperator,
} from '@mui/x-data-grid-pro';
import { createElement, useCallback, useMemo, useState } from 'react';

import type { AnalyticalDatasetResultRow } from '../api/types';
import { useAnalyticalDatasetResultsQuery } from '../api/useAnalyticalDatasetResultsQuery';
import { PendingResultsColumnHeader } from '../components/PendingResultsColumnHeader';
import { formatIsoDate } from '../utils/formatIsoDate';

const EMPTY_CELL = '—';
const SKELETON_COLUMN_MIN_WIDTH = 200;

// System demographic columns hidden by default once the dataset has any
// analytical variable — they'd otherwise crowd out the variable columns the
// user came to see. patient_id + index_date always stay visible. When there are
// no variables, all of these show by default.
const DEMOGRAPHIC_FIELDS = ['age_at_index', 'gender', 'race', 'ethnicity'] as const;

// Default sort mirrors the server's fallback ordering (patient_id ascending);
// patient_id tiebreaks non-unique sorts so pagination stays deterministic.
export const RESULTS_DEFAULT_SORT_MODEL = [{ field: 'patient_id', sort: 'asc' as const }];

// Only the operators the results endpoint translates to SQL: isEmpty/isNotEmpty
// serialize without a value and isAnyOf's array value breaks the
// `field:operator(value)` wire format, so offering them would show a "filtered"
// grid the server never filtered.
const SUPPORTED_STRING_OPERATORS = new Set([
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  'startsWith',
  'endsWith',
]);
const SUPPORTED_NUMERIC_OPERATORS = new Set(['=', '!=', '>', '>=', '<', '<=']);
const SUPPORTED_DATE_OPERATORS = new Set([
  'is',
  'not',
  'after',
  'onOrAfter',
  'before',
  'onOrBefore',
]);

// Placeholder columns rendered before the first response arrives so the grid's
// loading overlay draws per-column skeleton cells. They mirror the stable system
// prefix of the server column list, so the header row doesn't jump when the
// real columns swap in.
const SYSTEM_SKELETON_COLUMNS: GridColDef<AnalyticalDatasetResultRow>[] = [
  { field: 'patient_id', headerName: 'Patient ID' },
  { field: 'index_date', headerName: 'Index date', type: 'date' },
  { field: 'age_at_index', headerName: 'Age', type: 'number' },
  { field: 'gender', headerName: 'Gender' },
  { field: 'race', headerName: 'Race' },
  { field: 'ethnicity', headerName: 'Ethnicity' },
];

// Boolean values arrive as the raw strings "true"/"false"; show them title-cased.
const formatBooleanCell = (value: string): string => {
  const normalized = value.toLowerCase();
  if (normalized === 'true') return 'True';
  if (normalized === 'false') return 'False';
  return value;
};

/** A definition source the processed run has no column for. */
export interface PendingResultsColumn {
  key: string;
  title: string;
}

// Prefix keeps a pending column's field clear of the server's column fields.
const PENDING_COLUMN_FIELD_PREFIX = 'pending:';

interface UseAnalyticalDatasetResultsArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** Whether the dataset's definition (saved or staged) has any analytical
   * variable; hides the demographic columns by default when it does. */
  hasAnalyticalVariables?: boolean;
  /** Columns appended after the server's, with no cells, so the grid lists
   * every source of the current definition. */
  pendingColumns?: PendingResultsColumn[];
  pendingCaption?: string;
}

const NO_PENDING_COLUMNS: PendingResultsColumn[] = [];

/**
 * Server-mode DataGrid wiring for an analytical dataset's per-patient results:
 * the paged fetch, server columns -> GridColDef mapping and the filter
 * operators the backend supports.
 */
export const useAnalyticalDatasetResults = ({
  projectId,
  analyticalDatasetProfileId,
  hasAnalyticalVariables = false,
  pendingColumns = NO_PENDING_COLUMNS,
  pendingCaption = '',
}: UseAnalyticalDatasetResultsArgs) => {
  // Called as a hook by the shared DataGrid with its serialized grid params.
  const useFetchResultsGridData = useCallback(
    (searchParams?: string) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks -- invoked as a hook inside DataGrid's render
      useAnalyticalDatasetResultsQuery({ projectId, analyticalDatasetProfileId, searchParams }),
    [projectId, analyticalDatasetProfileId]
  );

  const stringOperators = useMemo(
    () => getGridStringOperators().filter((op) => SUPPORTED_STRING_OPERATORS.has(op.value)),
    []
  );
  const numericOperators = useMemo(
    () => getGridNumericOperators().filter((op) => SUPPORTED_NUMERIC_OPERATORS.has(op.value)),
    []
  );
  const dateOperators = useMemo(
    () => getGridDateOperators().filter((op) => SUPPORTED_DATE_OPERATORS.has(op.value)),
    []
  );

  // Date values arrive as ISO YYYY-MM-DD strings and are formatted for display only.
  const mapServerColumns = useCallback(
    (
      serverColumns: GridColDef<AnalyticalDatasetResultRow>[]
    ): GridColDef<AnalyticalDatasetResultRow>[] => {
      const resultColumns = (
        serverColumns.length === 0 ? SYSTEM_SKELETON_COLUMNS : serverColumns
      ).map<GridColDef<AnalyticalDatasetResultRow>>((column) => {
        const isDate = column.type === 'date' || column.type === 'dateTime';
        const isDateTime = column.type === 'dateTime';
        const isBoolean = column.type === 'boolean';
        const filterOperators: GridFilterOperator[] | undefined =
          column.type === 'number'
            ? numericOperators
            : column.type === 'date'
            ? dateOperators
            : isBoolean || isDateTime
            ? undefined
            : stringOperators;
        return {
          field: column.field,
          headerName: column.headerName || column.field,
          // MUI's 'date' type expects Date objects; keep the string values.
          type: isDate ? undefined : column.type,
          minWidth: column.minWidth ?? SKELETON_COLUMN_MIN_WIDTH,
          flex: 1,
          sortable: true,
          filterable: !isDateTime,
          ...(filterOperators ? { filterOperators } : {}),
          // Header hover: the full display name plus the raw column key.
          description: `${column.headerName || column.field} (${column.field})`,
          hideable: column.field !== 'patient_id',
          renderCell: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return EMPTY_CELL;
            }
            if (isDate) return formatIsoDate(String(params.value));
            if (isBoolean) return formatBooleanCell(String(params.value));
            return String(params.value);
          },
        };
      });

      const pendingGridColumns = pendingColumns.map<GridColDef<AnalyticalDatasetResultRow>>(
        ({ key, title }) => ({
          field: `${PENDING_COLUMN_FIELD_PREFIX}${key}`,
          headerName: title,
          minWidth: SKELETON_COLUMN_MIN_WIDTH,
          flex: 1,
          sortable: false,
          filterable: false,
          renderHeader: () =>
            createElement(PendingResultsColumnHeader, { title, caption: pendingCaption }),
          renderCell: () => EMPTY_CELL,
        })
      );
      return [...resultColumns, ...pendingGridColumns];
    },
    [stringOperators, numericOperators, dateOperators, pendingColumns, pendingCaption]
  );

  // Default demographic visibility: hidden when variables are present, shown
  // otherwise. Controlled so the user's column-toggle choices override the
  // default once made.
  const defaultVisibility = useMemo<GridColumnVisibilityModel>(
    () =>
      hasAnalyticalVariables
        ? Object.fromEntries(DEMOGRAPHIC_FIELDS.map((field) => [field, false]))
        : {},
    [hasAnalyticalVariables]
  );
  const [visibilityOverride, onColumnVisibilityModelChange] =
    useState<GridColumnVisibilityModel | null>(null);
  const columnVisibilityModel = visibilityOverride ?? defaultVisibility;

  return {
    mapServerColumns,
    useFetchResultsGridData,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  };
};
